import React from "react";

import "./options/OutputOptions.css";

export function OutputOptionsPanel({
  opts,
  isPro,
  lowContrast,
  contrastRatio,
  onChange,
  onPreset,
}: {
  opts: any;
  isPro: boolean;
  lowContrast?: boolean;
  contrastRatio?: number;
  onChange: (patch: Partial<any>) => void;
  onPreset: (name: string) => void;
}) {
  const moduleIsRounded = (opts.moduleShape ?? "square") === "rounded";

  return (
    <section className="oo-section">
      {/* Free Core */}
      <div className="oo-card">
        <div className="oo-card__header">
          <h3 className="oo-card__title">QR Core <span className="oo-badge oo-badge--free">Free</span></h3>
        </div>
        <div className="oo-grid">
          {/* ECC */}
          <div className="oo-field">
            <label htmlFor="ecc">ECC</label>
            <select id="ecc" value={opts.ecc} onChange={e => onChange({ ecc: e.target.value })}>
              <option value="L">L (7%)</option>
              <option value="M">M (15%)</option>
              <option value="Q">Q (25%)</option>
              <option value="H">H (30%)</option>
            </select>
          </div>

          {/* Quiet zone (applies to no-frame only) */}
          <div className="oo-field">
            <label htmlFor="marginModules">Quiet zone (no-frame only)</label>
            <input
              id="marginModules"
              type="number"
              min={0}
              max={12}
              value={opts.marginModules ?? 2}
              onChange={e => onChange({ marginModules: Number(e.target.value) })}
              disabled={opts.frameType && opts.frameType !== 'none'}
              title={opts.frameType && opts.frameType !== 'none' ? 'Disabled when a frame is selected' : undefined}
            />
          </div>

          {/* Frame Type */}
          <div className="oo-field">
            <label htmlFor="frameType">Frame</label>
            <select id="frameType" value={opts.frameType} onChange={e => onChange({ frameType: e.target.value })}>
              <option value="none">No frame</option>
              <option value="basic">Frame — Bottom label</option>
              <option value="tabBottom">Frame — Speech tab (below)</option>
              <option value="tabTop">Frame — Speech tab (above)</option>
              <option value="arrow">Frame — Arrow & “Scan me”</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pro Design Studio */}
      <div className={`oo-card ${!isPro ? "oo-card--locked" : ""}`} aria-disabled={!isPro} title={!isPro ? "Pro feature" : undefined}>
        <div className="oo-card__header">
          <h3 className="oo-card__title">Design Studio <span className="oo-badge oo-badge--pro">Pro</span></h3>
        </div>

        <div className="oo-grid">
          {/* Frame Color */}
          <div className="oo-field">
            <label htmlFor="frameColor">Frame Color</label>
            <input
              id="frameColor"
              type="color"
              value={opts.frameColor ?? "#000000"}
              onChange={e => onChange({ frameColor: (e.target as HTMLInputElement).value })}
              disabled={!isPro}
              title={isPro ? undefined : "Unlock Lifetime Pro to enable"}
            />
          </div>

          {/* QR Colors */}
          <div className="oo-field"><label htmlFor="bodyColor">Body</label>
            <input id="bodyColor" type="color" value={opts.bodyColor ?? "#000000"}
                   onChange={e => onChange({ bodyColor: (e.target as HTMLInputElement).value })} disabled={!isPro}
                   title={isPro ? undefined : "Unlock Lifetime Pro to enable"}/>
          </div>
          <div className="oo-field"><label htmlFor="bgColor">Background</label>
            <input id="bgColor" type="color" value={opts.bgColor ?? "#ffffff"}
                   onChange={e => onChange({ bgColor: (e.target as HTMLInputElement).value })} disabled={!isPro}
                   title={isPro ? undefined : "Unlock Lifetime Pro to enable"}/>
          </div>
          <div className="oo-field"><label htmlFor="eyeBorderColor">Eye border</label>
            <input id="eyeBorderColor" type="color" value={opts.eyeBorderColor ?? "#000000"}
                   onChange={e => onChange({ eyeBorderColor: (e.target as HTMLInputElement).value })} disabled={!isPro}
                   title={isPro ? undefined : "Unlock Lifetime Pro to enable"}/>
          </div>
          <div className="oo-field"><label htmlFor="eyeCenterColor">Eye center</label>
            <input id="eyeCenterColor" type="color" value={opts.eyeCenterColor ?? "#000000"}
                   onChange={e => onChange({ eyeCenterColor: (e.target as HTMLInputElement).value })} disabled={!isPro}
                   title={isPro ? undefined : "Unlock Lifetime Pro to enable"}/>
          </div>

          {/* Shapes */}
          <div className="oo-field"><label htmlFor="moduleShape">Module shape</label>
            <select id="moduleShape" value={opts.moduleShape ?? "square"}
                    onChange={e => onChange({ moduleShape: e.target.value })} disabled={!isPro}
                    title={isPro ? undefined : "Unlock Lifetime Pro to enable"}>
              <option value="square">Square</option>
              <option value="rounded">Rounded</option>
              <option value="dots">Dots</option>
            </select>
          </div>

          <div className="oo-field"><label htmlFor="eyeBorderShape">Eye border</label>
            <select id="eyeBorderShape" value={opts.eyeBorderShape ?? "square"}
                    onChange={e => onChange({ eyeBorderShape: e.target.value })} disabled={!isPro}
                    title={isPro ? undefined : "Unlock Lifetime Pro to enable"}>
              <option value="square">Square</option>
              <option value="rounded">Rounded</option>
              <option value="circle">Circle</option>
            </select>
          </div>

          <div className="oo-field"><label htmlFor="eyeCenterShape">Eye center</label>
            <select id="eyeCenterShape" value={opts.eyeCenterShape ?? "square"}
                    onChange={e => onChange({ eyeCenterShape: e.target.value })} disabled={!isPro}
                    title={isPro ? undefined : "Unlock Lifetime Pro to enable"}>
              <option value="square">Square</option>
              <option value="circle">Circle</option>
              <option value="diamond">Diamond</option>
            </select>
          </div>

          {moduleIsRounded ? (
            <div className="oo-field">
              <label htmlFor="roundness">Roundness</label>
              <input
                id="roundness"
                type="range"
                min={0}
                max={50}
                value={Math.round((opts.roundness ?? 0.35) * 100)}
                onChange={e => onChange({ roundness: parseInt(e.target.value, 10) / 100 })}
                disabled={!isPro}
                title={isPro ? undefined : "Unlock Lifetime Pro to enable"}
              />
            </div>
          ) : null}
        </div>

        {/* Presets row */}
        <div className="oo-presets">
          {["Monochrome","High Contrast","Brand Blue","Inverted"].map(p => (
            <button
              key={p}
              className="oo-preset"
              disabled={!isPro && p !== "Monochrome"}
              title={!isPro && p !== "Monochrome" ? "Unlock Lifetime Pro to enable" : undefined}
              onClick={() => onPreset(p)}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Low contrast warning */}
        {lowContrast ? (
          <div className="alert alert-warning" style={{ marginTop: 8 }}>
            <div>
              Low contrast ({(contrastRatio ?? 0).toFixed(2)}:1) may reduce scan reliability. Increase contrast or use dark on light.
            </div>
          </div>
        ) : null}
      </div>

      {/* Pro Export */}
      <div className={`oo-card ${!isPro ? "oo-card--locked" : ""}`} aria-disabled={!isPro} title={!isPro ? "Pro feature" : undefined}>
        <div className="oo-card__header">
          <h3 className="oo-card__title">Export <span className="oo-badge oo-badge--pro">Pro</span></h3>
        </div>
        <div className="oo-export-row">
          <div className="oo-field oo-field--inline">
            <label htmlFor="exportPng">Export PNG (Pro)</label>
            <input id="exportPng" type="checkbox"
              checked={!!opts.exportPng}
              onChange={e => onChange({ exportPng: e.target.checked })}
              disabled={!isPro}
              title={isPro ? undefined : "Unlock Lifetime Pro to enable"}
            />
          </div>
          <div className="oo-field oo-field--inline">
            <label htmlFor="pngSize">PNG size (px)</label>
            <input id="pngSize" className="oo-input--short" type="number" min={256} step={128}
              value={opts.pngSize ?? 1024}
              onChange={e => onChange({ pngSize: Number(e.target.value) })}
              disabled={!isPro || !opts.exportPng}
              title={!isPro ? "Unlock Lifetime Pro to enable" : (!opts.exportPng ? "Enable Export PNG first" : undefined)}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default OutputOptionsPanel;


